import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSearch, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useAdicionarItemAvulso, useAtualizarItem, useObterProdutoFiscalNFe } from '../../../../hooks/useNFe';
import { ItemNFe, AdicionarItemRequest, AtualizarItemRequest, BuscarProdutosNFeItem, RegraFiscalNFeItem } from '../../../../types/nfe';
import CfopInput from '../../components/CfopInput';
import ModalBuscarProdutoNFe from './ModalBuscarProdutoNFe';
import ModalBuscarRegraFiscalNFe from './ModalBuscarRegraFiscalNFe';
import LinkProduto from 'components/common/LinkProduto';

interface ModalItemNFeProps {
  show: boolean;
  onHide: () => void;
  nfeId: string;
  crt: string;
  empresaId: string;
  item?: ItemNFe;
}

type FormValues = AdicionarItemRequest & AtualizarItemRequest & { valorTotal?: number };

const buildDefaultValues = (item?: ItemNFe): FormValues => ({
  codigoProduto: item?.codigoProduto ?? '',
  nomeProduto: item?.nomeProduto ?? '',
  ncm: item?.ncm ?? '',
  cest: item?.cest,
  cfop: item?.cfop ?? '',
  unidadeComercial: item?.unidadeComercial ?? 'UN',
  quantidadeComercial: item?.quantidadeComercial ?? (undefined as unknown as number),
  valorUnitario: item?.valorUnitario ?? (undefined as unknown as number),
  valorDesconto: item?.valorDesconto,
  valorFrete: item?.valorFrete,
  valorSeguro: item?.valorSeguro,
  valorOutros: item?.valorOutros,
  produtoId: item?.produtoId,
  infAdProd: item?.infAdProd,
});

const numField = { setValueAs: (v: string) => (v === '' ? undefined : Number(v)) };

const ModalItemNFe: React.FC<ModalItemNFeProps> = ({ show, onHide, nfeId, crt: _crt, empresaId, item }) => {
  const { showToast } = useToast();
  const isEditMode = !!item;

  const [showBuscarProduto, setShowBuscarProduto] = useState(false);
  const [showBuscarRegra, setShowBuscarRegra] = useState(false);
  const [selectedProdutoId, setSelectedProdutoId] = useState<string | undefined>(undefined);
  const [selectedProdutoLabel, setSelectedProdutoLabel] = useState('');
  const [selectedRegraLabel, setSelectedRegraLabel] = useState('');
  const [selectedRegraId, setSelectedRegraId] = useState<string | undefined>(undefined);
  const [produtoJustSelected, setProdutoJustSelected] = useState(false);

  const adicionarMutation = useAdicionarItemAvulso(nfeId);
  const atualizarMutation = useAtualizarItem(nfeId, item?.id ?? '');
  const mutation = isEditMode ? atualizarMutation : adicionarMutation;

  const { register, handleSubmit, formState: { errors }, reset, watch, control, setValue } =
    useForm<FormValues>({ defaultValues: buildDefaultValues(item) });

  const fiscalQuery = useObterProdutoFiscalNFe(selectedProdutoId ?? '', empresaId, !!selectedProdutoId);

  useEffect(() => {
    if (show) {
      reset(buildDefaultValues(item));
      setSelectedProdutoId(item?.produtoId);
      setSelectedProdutoLabel(item?.produtoId ? `${item.codigoProduto} — ${item.nomeProduto}` : '');
      setSelectedRegraLabel(
        item?.regraFiscalCodigo && item?.regraFiscalDescricao
          ? `${item.regraFiscalCodigo} — ${item.regraFiscalDescricao}`
          : ''
      );
      setSelectedRegraId(item?.regraFiscalId);
      setProdutoJustSelected(false);
    }
  }, [show, item, reset]);

  const applyClassificacaoFiscal = (d: { cfop?: string; ncm?: string; cest?: string }) => {
    if (d.ncm) setValue('ncm', d.ncm.replace(/\D/g, ''));
    if (d.cest) setValue('cest', d.cest.replace(/\D/g, ''));
    if (d.cfop) setValue('cfop', d.cfop);
  };

  useEffect(() => {
    if (!fiscalQuery.data || !produtoJustSelected) return;
    setProdutoJustSelected(false);
    const d = fiscalQuery.data;
    setValue('codigoProduto', String(d.codigo));
    setValue('nomeProduto', d.descricao);
    setValue('valorUnitario', d.valor as unknown as number);
    if (d.unidade) setValue('unidadeComercial', d.unidade);
    setValue('produtoId', d.id);
    applyClassificacaoFiscal(d);
    if (d.regraFiscalCodigo && d.regraFiscalDescricao)
      setSelectedRegraLabel(`${d.regraFiscalCodigo} — ${d.regraFiscalDescricao}`);
    if (d.regraFiscalId) setSelectedRegraId(d.regraFiscalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalQuery.data]);

  const handleSelectProduto = (produto: BuscarProdutosNFeItem) => {
    setProdutoJustSelected(true);
    setSelectedProdutoId(produto.id);
    setSelectedProdutoLabel(`${produto.codigo} — ${produto.descricao}`);
  };

  const handleClearProduto = () => {
    setSelectedProdutoId(undefined);
    setSelectedProdutoLabel('');
    setValue('produtoId', undefined);
  };

  const handleSelectRegra = (regra: RegraFiscalNFeItem) => {
    setSelectedRegraLabel(`${regra.codigo} — ${regra.descricao}`);
    setSelectedRegraId(regra.id);
    applyClassificacaoFiscal(regra);
  };

  const quantidade = watch('quantidadeComercial');
  const unitario = watch('valorUnitario');

  const valorTotal = (Number(quantidade) || 0) * (Number(unitario) || 0);

  const onSubmit = async (data: FormValues) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (mutation as any).mutateAsync({ ...data, produtoId: selectedProdutoId || undefined, regraFiscalId: selectedRegraId });
      showToast(isEditMode ? 'Item atualizado' : 'Item adicionado', 'success');
      onHide();
    } catch {
      // error shown via Alert
    }
  };

  return (
    <>
      <Modal show={show && !showBuscarProduto && !showBuscarRegra} onHide={() => !mutation.isPending && onHide()} size="lg" centered>
        <Modal.Header closeButton className="border-bottom border-opacity-25 pb-3">
          <div>
            <div className="nfe-dest-section-title mb-1">
              {isEditMode ? 'Editar Item' : 'Novo Item'}
            </div>
            {isEditMode && item ? (
              <div className="d-flex align-items-baseline gap-2 flex-wrap">
                <span className="nfe-mono fw-semibold text-primary" style={{ fontSize: '0.78rem' }}>
                  {item.codigoProduto}
                </span>
                <span className="fw-semibold text-body-emphasis" style={{ fontSize: '0.88rem', lineHeight: 1.3 }}>
                  {item.nomeProduto}
                </span>
              </div>
            ) : (
              <span className="fw-semibold text-body-emphasis" style={{ fontSize: '0.88rem' }}>
                Preencha os dados do item
              </span>
            )}
          </div>
        </Modal.Header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="p-4">
            {mutation.isError && (
              <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle py-2 mb-3">
                <small className="text-danger-emphasis">Erro ao salvar item. Tente novamente.</small>
              </Alert>
            )}

            {/* ── Produto Cadastrado ──────────────────────── */}
            <div className="nfe-dest-section">
              <div className="nfe-dest-section-header">
                <span className="nfe-dest-section-title">Produto Cadastrado</span>
                <small className="text-body-secondary" style={{ fontSize: '0.72rem' }}>
                  Opcional — preenche campos automaticamente
                </small>
              </div>

              {selectedProdutoLabel ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'var(--bs-success-bg-subtle)',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--bs-success-border-subtle)',
                }}>
                  {fiscalQuery.isFetching
                    ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem', flexShrink: 0 }} />
                    : <FontAwesomeIcon icon={faCheck} style={{ color: 'var(--bs-success)', fontSize: '0.75rem', flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.82rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedProdutoId ? (
                      <LinkProduto produtoId={selectedProdutoId} showIcon className="text-body-emphasis">
                        {selectedProdutoLabel}
                      </LinkProduto>
                    ) : selectedProdutoLabel}
                  </span>
                  <button type="button" className="btn btn-link btn-sm p-0 text-body-secondary"
                    style={{ fontSize: '0.73rem', textDecoration: 'none' }}
                    onClick={() => setShowBuscarProduto(true)}
                    disabled={mutation.isPending}>
                    Alterar
                  </button>
                  <button type="button" className="btn btn-link btn-sm p-0 text-body-secondary ms-1"
                    onClick={handleClearProduto}
                    disabled={mutation.isPending}>
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                  </button>
                </div>
              ) : (
                <Button
                  variant="phoenix-secondary"
                  size="sm"
                  className="w-100"
                  style={{ borderStyle: 'dashed' }}
                  onClick={() => setShowBuscarProduto(true)}
                  disabled={mutation.isPending}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2" style={{ fontSize: '0.75rem' }} />
                  Buscar produto cadastrado...
                </Button>
              )}
            </div>

            {/* ── Produto ─────────────────────────────────── */}
            <div className="nfe-dest-section">
              <div className="nfe-dest-section-header">
                <span className="nfe-dest-section-title">Produto</span>
              </div>
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <label className="nfe-dest-label">Código *</label>
                    <Form.Control size="sm" type="text"
                      {...register('codigoProduto', { required: 'Obrigatório' })}
                      isInvalid={!!errors.codigoProduto} disabled={mutation.isPending} />
                    <Form.Control.Feedback type="invalid">{errors.codigoProduto?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Group>
                    <label className="nfe-dest-label">Descrição *</label>
                    <Form.Control size="sm" type="text"
                      {...register('nomeProduto', { required: 'Obrigatório' })}
                      isInvalid={!!errors.nomeProduto} disabled={mutation.isPending} />
                    <Form.Control.Feedback type="invalid">{errors.nomeProduto?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* ── Quantidades e Valores ───────────────────── */}
            <div className="nfe-dest-section">
              <div className="nfe-dest-section-header">
                <span className="nfe-dest-section-title">Quantidades e Valores</span>
              </div>
              <Row className="g-3">
                <Col xs={4} md={2}>
                  <Form.Group>
                    <label className="nfe-dest-label">Unidade *</label>
                    <Form.Control size="sm" type="text"
                      {...register('unidadeComercial', { required: 'Obrigatório' })}
                      isInvalid={!!errors.unidadeComercial} disabled={mutation.isPending} placeholder="UN" maxLength={6} />
                    <Form.Control.Feedback type="invalid">{errors.unidadeComercial?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={8} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Quantidade *</label>
                    <Form.Control size="sm" type="number" step="0.0001"
                      {...register('quantidadeComercial', { valueAsNumber: true, required: 'Obrigatório', min: { value: 0.0001, message: '> 0' } })}
                      isInvalid={!!errors.quantidadeComercial} disabled={mutation.isPending} />
                    <Form.Control.Feedback type="invalid">{errors.quantidadeComercial?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Vlr. Unit. *</label>
                    <Form.Control size="sm" type="number" step="0.0001"
                      {...register('valorUnitario', { valueAsNumber: true, required: 'Obrigatório', min: { value: 0.001, message: '> 0' } })}
                      isInvalid={!!errors.valorUnitario} disabled={mutation.isPending} />
                    <Form.Control.Feedback type="invalid">{errors.valorUnitario?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group>
                    <label className="nfe-dest-label">Total</label>
                    <div className="form-control form-control-sm bg-body-secondary text-end fw-semibold text-primary-emphasis" style={{ cursor: 'default' }}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorTotal)}
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Desconto</label>
                    <Form.Control size="sm" type="number" step="0.01" {...register('valorDesconto', numField)} disabled={mutation.isPending} placeholder="0,00" />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Frete</label>
                    <Form.Control size="sm" type="number" step="0.01" {...register('valorFrete', numField)} disabled={mutation.isPending} placeholder="0,00" />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Seguro</label>
                    <Form.Control size="sm" type="number" step="0.01" {...register('valorSeguro', numField)} disabled={mutation.isPending} placeholder="0,00" />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <label className="nfe-dest-label">Outros</label>
                    <Form.Control size="sm" type="number" step="0.01" {...register('valorOutros', numField)} disabled={mutation.isPending} placeholder="0,00" />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* ── Regra Fiscal ────────────────────────────── */}
            <div className="nfe-dest-section">
              <div className="nfe-dest-section-header">
                <span className="nfe-dest-section-title">Regra Fiscal</span>
                <small className="text-body-secondary" style={{ fontSize: '0.72rem' }}>
                  Opcional — tributação calculada automaticamente
                </small>
              </div>

              {selectedRegraLabel ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'var(--bs-info-bg-subtle)',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--bs-info-border-subtle)',
                }}>
                  <FontAwesomeIcon icon={faCheck} style={{ color: 'var(--bs-info)', fontSize: '0.75rem', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.82rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedRegraId ? (
                      <a
                        href={`/cadastros/regras-fiscais/${selectedRegraId}`}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); window.open(`/cadastros/regras-fiscais/${selectedRegraId}`, '_blank'); }}
                        className="text-body-emphasis text-decoration-none"
                        title="Abrir regra fiscal"
                      >
                        {selectedRegraLabel}
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" style={{ fontSize: '0.65rem' }} />
                      </a>
                    ) : selectedRegraLabel}
                  </span>
                  <button type="button" className="btn btn-link btn-sm p-0 text-body-secondary"
                    style={{ fontSize: '0.73rem', textDecoration: 'none' }}
                    onClick={() => setShowBuscarRegra(true)}
                    disabled={mutation.isPending}>
                    Alterar
                  </button>
                  <button type="button" className="btn btn-link btn-sm p-0 text-body-secondary ms-1"
                    onClick={() => { setSelectedRegraLabel(''); setSelectedRegraId(undefined); }}
                    disabled={mutation.isPending}>
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                  </button>
                </div>
              ) : (
                <Button
                  variant="phoenix-secondary"
                  size="sm"
                  className="w-100"
                  style={{ borderStyle: 'dashed' }}
                  onClick={() => setShowBuscarRegra(true)}
                  disabled={mutation.isPending}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2" style={{ fontSize: '0.75rem' }} />
                  Buscar regra fiscal...
                </Button>
              )}
            </div>

            {/* ── Classificação Fiscal ────────────────────── */}
            <div className="nfe-dest-section">
              <div className="nfe-dest-section-header">
                <span className="nfe-dest-section-title">Classificação Fiscal</span>
              </div>
              <Row className="g-3">
                <Col xs={6} md={4}>
                  <Form.Group>
                    <label className="nfe-dest-label">NCM *</label>
                    <Form.Control size="sm" type="text"
                      {...register('ncm', { required: 'Obrigatório', pattern: { value: /^\d{8}$/, message: '8 dígitos' } })}
                      isInvalid={!!errors.ncm} disabled={mutation.isPending} placeholder="00000000" maxLength={8} />
                    <Form.Control.Feedback type="invalid">{errors.ncm?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group>
                    <label className="nfe-dest-label">CFOP *</label>
                    <Controller
                      name="cfop"
                      control={control}
                      rules={{ required: 'Obrigatório' }}
                      render={({ field }) => (
                        <CfopInput
                          size="sm"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={mutation.isPending}
                          isInvalid={!!errors.cfop}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">{errors.cfop?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group>
                    <label className="nfe-dest-label">CEST</label>
                    <Form.Control size="sm" type="text" {...register('cest')}
                      disabled={mutation.isPending} placeholder="0000000" maxLength={7} />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* ── Informações Adicionais do Produto ────────── */}
            {isEditMode && (
              <div className="nfe-dest-section">
                <div className="nfe-dest-section-header">
                  <span className="nfe-dest-section-title">Informações Adicionais do Produto</span>
                  <small className="text-body-secondary" style={{ fontSize: '0.72rem' }}>
                    Opcional — campo infAdProd do XML (máx. 500 chars)
                  </small>
                </div>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    size="sm"
                    rows={3}
                    maxLength={500}
                    {...register('infAdProd')}
                    disabled={mutation.isPending}
                    placeholder="Norma referenciada, informações complementares..."
                  />
                </Form.Group>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="border-top border-opacity-25 py-2">
            <Button variant="phoenix-secondary" size="sm" onClick={() => !mutation.isPending && onHide()} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={mutation.isPending} loadingPosition="start">
              {isEditMode ? 'Atualizar' : 'Adicionar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ModalBuscarProdutoNFe
        show={showBuscarProduto}
        onHide={() => setShowBuscarProduto(false)}
        onSelecionar={handleSelectProduto}
      />

      <ModalBuscarRegraFiscalNFe
        show={showBuscarRegra}
        onHide={() => setShowBuscarRegra(false)}
        onSelecionar={handleSelectRegra}
      />
    </>
  );
};

export default ModalItemNFe;
